/**
 * Reusable scripts for our blocks
 */


const bookCoverStyle = window?.DP?.book_cover_background;
const isAudio = bookCoverStyle === 'Audio';

// Book functions
const handleBookCoverImage = ( item, contextEntry ) => {
  const isSearchResultsPage =
        window.location.href.includes( '/search/?keyword=' );
  const autoApiImage = isSearchResultsPage
    ? `https://images.penguinrandomhouse.com/cover/${item?.isbnStr}`
    : `https://images.penguinrandomhouse.com/cover/${item?.isbn}`;
  const manualImage = item?.uploadedImage?.url
    ? item.uploadedImage.url
    : item?.apiImage;
  return contextEntry === 'automatic' ? autoApiImage : manualImage;
};

const handleBookAuthors = ( item, contextEntry ) => contextEntry === 'automatic' ? handleBookAutoAuthors( item ) : item?.author;
const handleBookAutoAuthors = ( item ) => {
  const authors = item?.author || item?.authors;
  let authorsString = '';
  let str;
  
  if ( typeof authors === 'string' ) {
    return authors;
  }

  authors?.forEach( ( author ) => {
    if ( author?.roleCode === 'A' || author?.contribRoleCode === 'A' ) {
      authorsString += `${author.authorDisplay}, `;
      str = authorsString.replace( /,\s*$/, '' );
    }
  });

  return str;
};
const audioAllowedContributorUpdate = ( allowedContributorRoles ) => {
  const narratorRole = allowedContributorRoles.find(
    ( role ) => role.label === 'Narrator'
  );
  const hasReadBy = allowedContributorRoles.find(
    ( role ) => role.value === '4'
  );
  if ( narratorRole ) {
    narratorRole.label = 'Read by';
    narratorRole.value = 'Y';
  }
  // if there is a narrator role, add another object with the label "Read by" and the value "4"
  if ( narratorRole && !hasReadBy ) {
    allowedContributorRoles.push({ value: '4', label: 'Read by' });
  }
};

const getRoleName = ( roleCode ) => {
  const allowedContributorRoles = window?.DP?.contributor_roles;
  const role = allowedContributorRoles.find(
    ( role ) => role.value === roleCode
  );
  const roleName = role ? role.label : '';
  return roleName;
};

const handleAudioContributors = ( item, isAutomatic ) => {
  if ( !isAudio ) return;
  // authors & other contributors are listed in separate attributes for search; combine into one array
  let autoContributors = [];
  if ( isAutomatic ) {
    const authors = ( item?.authors || item?.author ) ?? [];
    const otherContributors = item?.otherContributors ?? [];
    autoContributors = [ ...authors, ...otherContributors ];
  }

  const contributors = isAutomatic
    ? autoContributors
    : item?.audioContributors;

  // account for auto grid which returns an array of authors
  if ( !contributors ) {
    if ( !item.authors ) return false;
    const arr = [];
    item.authors.forEach( ( author ) => {
      if ( author.contribRoleCode === 'A' ) {
        arr.push( author.authorDisplay );
      }
    });
    return arr;
  }

  // get allowed contributor roles directly from window.DP (that info isn't included with items from search)
  const allowedContributorRoles = window?.DP?.contributor_roles;
  audioAllowedContributorUpdate( allowedContributorRoles );

  let allAudioContributors = [];
  for ( let key in contributors ) {
    let contributor = contributors[ key ];
    // go through each contributor object and get their "display" and "roleName" values
    if ( isAutomatic ) {
      const { authorDisplay, contribRoleCode, roleCodeDesc, roleCode } = contributor;
      // push the authorDisplay and contribRoleCode values into an array
      allAudioContributors.push({ display: authorDisplay, roleName: roleCodeDesc || getRoleName( contribRoleCode ), roleCode: roleCode || contribRoleCode });
    } else {
      const { display, roleName, roleCode } = contributor;
      // push the display and roleName values into an array
      allAudioContributors.push({ display, roleName, roleCode });
    }
  }
  // cycle through allAudioContributors and if their roleName is in the allowedContributorRoles array, return the display value
  allAudioContributors = allAudioContributors.filter( ( contributor ) => {
    return allowedContributorRoles.some( ( allowedRole ) => {
      if ( allowedRole.value === contributor.roleCode ) {
        return contributor;
      } else {
        return false;
      }
    });
  });
  // any object with the same roleCode should be combined into one object with the display values separated by a comma
  allAudioContributors = allAudioContributors.reduce( ( acc, contributor ) => {
    const existingContributor = acc.find( ( item ) => item.roleCode === contributor.roleCode );
    if ( existingContributor ) {
      // set default count so we can track the reducer
      if ( !existingContributor.count ) {
        existingContributor.count = 1;
      }
      // if  its less than three just add the display value to the existing display value
      if ( existingContributor.count < 3 ) {
        existingContributor.display = `${existingContributor.display}, ${contributor.display}`;
      }
      // if its more than 3 add the "& more..." to the display value. This is the last if so it will ignore the rest
      if ( existingContributor.count === 3 ) {
        existingContributor.display = `${existingContributor.display} & more...`;
      }
      existingContributor.count = ( existingContributor.count || 1 ) + 1;
    } else {
      acc.push( contributor );
    }
    return acc;
  }, [] );

  // reorder contributors so that the author is always first and the rest follow an order specified by the allowedContributorRoles array
  allAudioContributors = allAudioContributors.sort( ( a, b ) => {
    return allowedContributorRoles.findIndex(
      ( role ) => role.value === a.roleCode
    ) - allowedContributorRoles.findIndex(
      ( role ) => role.value === b.roleCode
    );
  });

  // build a new contributors string from the filtered allAudioContributors array which always puts the author first then returns any other contributors
  let audioAuthors = '';
  allAudioContributors.forEach( ( contributor ) => {
    // decode the display value
    contributor.display = decodeURIComponent( contributor.display );
    if ( contributor.roleCode === 'A' ) {
      audioAuthors += `${contributor.display}; `;
    } else {
      audioAuthors += `${contributor.roleName} ${contributor.display}; `;
    }
  });
  // remove last semicolon
  if ( audioAuthors.endsWith( '; ' ) ) {
    audioAuthors = audioAuthors.slice( 0, -2 );
  }
  return audioAuthors;
};

const handleBookReadMoreURL = ( item ) => {
  if ( isAudio ) {
    if ( item?.isbn ) return `https://penguinrandomhouse.com/books/isbn/${item?.isbn}`;
    if ( item?.isbnStr ) return `https://penguinrandomhouse.com/books/isbn/${item?.isbnStr}`;
  }
  if ( item?.key ) return `https://penguinrandomhouse.com/books/${item.key}`;
  if ( item?.workId )
    return `https://penguinrandomhouse.com/books/${item.workId}`;
};

// Contributor Functions
const handleContributorAuthorRoles = ( item, context ) => {
  let arr = [];
  const r = item.contribRoles?.map( ( role ) => role.code );
  const allowedRoles = JSON.parse( context.allowedContributorRoles );
  // MPI-578: Convert the role 'Read by' to 'Narrator' if Narrator is allowed
  if ( allowedRoles.some( ( role ) => role.label === 'Narrator' ) ) {
    allowedRoles.push({ value: '4', label: 'Narrator' });
  }

  if ( allowedRoles === undefined ) return arr;
  arr = allowedRoles
    .map( ( item ) =>
      r !== undefined && r.includes( item.value ) ? item.label : null
    )
    .filter( ( filteredItem ) => filteredItem !== null );
  return context.entry === 'automatic'
    ? arr.join( ', ' )
    : item?.authorRoles?.join( ', ' );
};
const handleContributorTabAuthorRoles = ( item ) => {
  if ( item.roles === undefined ) {
    return false;
  }
  const roles = {
    A: 'Author',
    E: 'Editor',
    Y: 'Narrator',
    I: 'Illustrator',
    P: 'Photographer',
  };

  const arr = [];
  const authorRoles = Object.keys( item.roles );

  for ( const [ key, value ] of Object.entries( roles ) ) {
    if ( authorRoles.includes( key ) ) {
      arr.push( value );
    }
  }
  return arr.join( ', ' );
};
const handleContributorImage = ( item ) => {
  let contributorImage = `https://images.penguinrandomhouse.com/cover/${item?.authorOf?.isbn}?blur_rad=0&blur_sig=20`;
  if ( item.hasAuthorPhoto ) contributorImage = `https://images.penguinrandomhouse.com/author/${item.authorId}`;
  if ( item.apiImage ) contributorImage = item.apiImage;
  return contributorImage;
};
const isEncoded = ( str ) => {
  try {
    return typeof str == 'string' && decodeURIComponent( str ) !== str;
  } catch ( e ) {
    return false;
  }
};

// Focus trap utilities
const getFocusableElements = ( container ) => {
  return Array.from(
    container.querySelectorAll(
      'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )
  ).filter(
    el =>
      !!( el.offsetWidth || el.offsetHeight || el.getClientRects().length ) && // not display:none
      window.getComputedStyle( el ).visibility !== 'hidden' &&                // not visibility:hidden
      !el.hasAttribute( 'hidden' )                                            // not [hidden]
  );
};

const trapFocus = ( drawer ) => {
  let focusableEls = getFocusableElements( drawer );

  // MutationObserver to update focusableEls when content changes
  const observer = new MutationObserver( () => {
    focusableEls = getFocusableElements( drawer );
  });
  observer.observe( drawer, { childList: true, subtree: true });

  const handleKeyDown = ( e ) => {
    // Always get the latest focusable elements
    focusableEls = getFocusableElements( drawer );
    if ( !focusableEls.length ) return;
    const firstEl = focusableEls[0];
    const lastEl = focusableEls[focusableEls.length - 1];

    // Handle Tab and Shift+Tab for focus trapping
    if ( e.key === 'Tab' ) {
      if ( e.shiftKey ) {
        if ( document.activeElement === firstEl ) {
          e.preventDefault();
          lastEl.focus();
        }
      } else {
        if ( document.activeElement === lastEl ) {
          e.preventDefault();
          firstEl.focus();
        }
      }
    }
    if ( e.key === 'Escape' ) {
      // Optionally, you may want to provide a callback for Escape
      // or handle it in the consumer
    }
  };

  drawer.__trapFocusHandler = handleKeyDown;
  drawer.__trapFocusObserver = observer;
  drawer.addEventListener( 'keydown', handleKeyDown );
};

const removeTrapFocus = ( drawer ) => {
  if ( drawer && drawer.__trapFocusHandler ) {
    drawer.removeEventListener( 'keydown', drawer.__trapFocusHandler );
    delete drawer.__trapFocusHandler;
  }
  if ( drawer && drawer.__trapFocusObserver ) {
    drawer.__trapFocusObserver.disconnect();
    delete drawer.__trapFocusObserver;
  }
};

export {
  handleAudioContributors, handleBookAuthors, handleBookAutoAuthors, handleBookCoverImage, handleBookReadMoreURL,
  handleContributorAuthorRoles, handleContributorImage,
  handleContributorTabAuthorRoles, isEncoded,
  trapFocus, removeTrapFocus, getFocusableElements
};
